import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Keep the fetched Quick Add markup unchanged. Cloning the product-page
          // candy-mix-selector into it causes Horizon's modal morph to fail before
          // the dialog can open. The standalone picker is mounted after the modal
          // opens, using the product data carried by this card.
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Horizon must be allowed to render its normal Quick Add markup first.
      // A mix-picker failure must never prevent the modal from opening.
      try {
        const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
        await this.updateQuickAddModal(freshContent);
        this.#updateVariantPicker(productGrid);
      } catch (error) {
        console.warn('[Quick Add] Could not prepare modal content.', error);
      }
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);
    dialogComponent.showDialog();

    // Horizon finishes connecting and moving the modal form after showDialog().
    // Mount once more on the next frame so a Quick Add cannot lose its mix picker.
    window.requestAnimationFrame(() => {
      const modalContent = document.getElementById('quick-add-modal-content');
      if (modalContent) {
        this.#mountMixPicker(modalContent).catch((error) => console.warn('[Quick Add Mix] Mount failed.', error));
      }
    });
    window.setTimeout(() => {
      const modalContent = document.getElementById('quick-add-modal-content');
      if (modalContent) {
        this.#mountMixPicker(modalContent).catch((error) => console.warn('[Quick Add Mix] Mount failed.', error));
      }
    }, 250);

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid, mixSelectorMarkup = '') {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    // Do not mount the mix picker while Horizon is morphing this content.
    // It is mounted only after the dialog is open in #openQuickAddModal.
    this.#syncVariantSelection(modalContent);
  }

  /**
   * Mounts a standalone 1–5 flavor picker inside the Quick Add form.
   * The source selector is used only to identify tagged mix products and obtain
   * their actual flavor values; the popup itself uses normal DOM controls.
   * @param {Element} modalContent
   * @param {string} mixSelectorMarkup
   */
  async #mountMixPicker(modalContent) {
    const form = modalContent.querySelector('product-form-component form') || modalContent.querySelector('form[action*="/cart/add"]') || modalContent.querySelector('form');
    const picker = modalContent.querySelector('variant-picker');
    const productUrl = this.productPageUrl || this.closest('product-card')?.querySelector('a[href*="/products/"]')?.href || '';
    if (!form || !productUrl) return;

    // Quick Add receives the exact flavor list from its product card. This avoids
    // Horizon replacing the fetched product markup before the picker can mount.
    let optionName = String(this.dataset.mixOptionName || '').trim();
    let flavors = [];
    try {
      const embeddedFlavors = JSON.parse(this.dataset.mixFlavors || '[]');
      if (Array.isArray(embeddedFlavors)) flavors = [...new Set(embeddedFlavors.map(String).filter(Boolean))];
    } catch (error) {
      // Use the product JSON fallback below if an older card has no embedded data.
    }

    if (!optionName || !flavors.length) {
      let product;
      try {
        const url = new URL(productUrl, window.location.origin);
        url.search = '';
        url.hash = '';
        url.pathname = url.pathname.replace(/\/$/, '') + '.js';
        const response = await fetch(url.toString(), { credentials: 'same-origin' });
        if (!response.ok) return;
        product = await response.json();
      } catch (error) {
        console.warn('[Quick Add Mix] Could not load product options.', error);
        return;
      }

      const optionNames = Array.isArray(product.options) ? product.options : [];
      const optionIndex = optionNames.findIndex((name) => /^(flavor|flavors|flavour|flavours|candy|candies)$/i.test(String(name).trim()));
      if (optionIndex < 0) return;
      optionName = optionNames[optionIndex];
      flavors = [...new Set((product.variants || []).map((variant) => variant.options?.[optionIndex]).filter(Boolean))];
    }
    if (!optionName || !flavors.length) return;

    const sameOption = (element) => {
      const name = element?.dataset?.optionName || element?.getAttribute?.('name') || '';
      return String(name).toLowerCase().includes(String(optionName).toLowerCase());
    };

    modalContent.querySelectorAll('.ary-quick-mix-picker').forEach((element) => element.remove());
    form.querySelectorAll('input[data-ary-quick-mix-property]').forEach((element) => element.remove());

    const nativeControls = picker ? Array.from(picker.querySelectorAll('[data-option-name], select[name*="options"], input[name*="options"]')) : [];
    const nativeFlavor = nativeControls.find(sameOption);
    nativeFlavor?.closest('.variant-option, fieldset, .variant-picker__option')?.style.setProperty('display', 'none', 'important');

    if (!document.getElementById('ary-quick-mix-picker-styles')) {
      const styles = document.createElement('style');
      styles.id = 'ary-quick-mix-picker-styles';
      styles.textContent = '.ary-quick-mix-picker{margin:14px 0;padding:15px;border:1px solid rgba(168,85,247,.32);border-radius:16px;background:linear-gradient(135deg,#fff7fc,#f4edff 55%,#effbff);box-shadow:0 9px 24px rgba(112,50,151,.1)}.ary-quick-mix-picker h3{margin:0;color:#6e42b2;font-size:1rem}.ary-quick-mix-picker p{margin:5px 0 12px;color:#6f5876;font-size:.86rem}.ary-quick-mix-choices{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px}.ary-quick-mix-choice{display:flex;align-items:center;gap:8px;min-height:40px;padding:8px 9px;border:1px solid rgba(168,85,247,.22);border-radius:11px;background:#fff;color:#4d3157;cursor:pointer;font-size:.84rem;font-weight:700}.ary-quick-mix-choice:has(input:checked){border-color:#a855f7;background:#f5edff;box-shadow:0 0 0 2px rgba(168,85,247,.12)}.ary-quick-mix-choice:has(input:disabled){opacity:.45;cursor:not-allowed}.ary-quick-mix-choice input{width:16px;height:16px;accent-color:#a855f7}.ary-quick-mix-message{min-height:1.2em;margin-top:10px!important;color:#7a41bc!important;font-weight:800}@media(max-width:520px){.ary-quick-mix-choices{grid-template-columns:1fr}}';
      document.head.appendChild(styles);
    }

    const panel = document.createElement('section');
    panel.className = 'ary-quick-mix-picker';
    panel.innerHTML = '<h3>Select 1 or More ' + optionName + '</h3><p>Choose up to 5 ' + optionName.toLowerCase() + ' for this bag.</p><div class="ary-quick-mix-choices"></div><p class="ary-quick-mix-message" aria-live="polite"></p>';
    const choices = panel.querySelector('.ary-quick-mix-choices');
    const message = panel.querySelector('.ary-quick-mix-message');

    const property = document.createElement('input');
    property.type = 'hidden';
    property.name = 'properties[Selected Mix]';
    property.dataset.aryQuickMixProperty = 'true';
    form.appendChild(property);

    flavors.forEach((flavor) => {
      const label = document.createElement('label');
      label.className = 'ary-quick-mix-choice';
      const input = document.createElement('input');
      input.type = 'checkbox';
      input.value = flavor;
      label.append(input, document.createTextNode(flavor));
      choices.append(label);
    });

    // Keeping it inside the actual add-to-cart form prevents Horizon from
    // moving it outside the visible Quick Add layout during dialog hydration.
    form.insertBefore(panel, form.querySelector('button[type="submit"], .add-to-cart-button') || null);
    const checks = Array.from(panel.querySelectorAll('input[type="checkbox"]'));

    const selectAnchorVariant = (flavor) => {
      const input = nativeControls.find((element) => sameOption(element) && element.value === flavor);
      if (input instanceof HTMLInputElement && input.type === 'radio' && !input.checked) {
        input.click();
        return;
      }
      const select = input?.closest('select') || (input instanceof HTMLSelectElement ? input : null);
      if (select instanceof HTMLSelectElement && select.value !== flavor) {
        select.value = flavor;
        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    };

    const refresh = () => {
      const selected = checks.filter((input) => input.checked).map((input) => input.value);
      property.value = selected.join(', ');
      checks.forEach((input) => { input.disabled = selected.length >= 5 && !input.checked; });
      message.textContent = selected.length ? selected.length + ' selected' : '';
      if (selected.length) selectAnchorVariant(selected[selected.length - 1]);
    };

    checks.forEach((input) => input.addEventListener('change', refresh));
    form.addEventListener('submit', (event) => {
      if (checks.some((input) => input.checked)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      message.textContent = 'Please select at least 1 ' + optionName.toLowerCase() + '.';
      panel.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, true);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
